import type {
  AgreedPolicy,
  ParentPoliciesField,
  PolicyGroup,
} from '~entities/parent/model/parent.type';

/**
 * Parent `policies` is stored inconsistently in Mongo:
 * - array of `{ agreedPolicies: [...] }` groups (typed FE shape)
 * - single object `{ loadState?, agreedPolicies: [...] }` (registration / hub)
 * - flat array of agreed policy rows
 */
export type { ParentPoliciesField };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const looksLikePolicyRow = (value: unknown): value is AgreedPolicy => {
  if (!isRecord(value)) return false;
  return 'policyId' in value || 'status' in value || 'title' in value;
};

const looksLikePolicyGroup = (value: unknown): value is PolicyGroup => {
  if (!isRecord(value)) return false;
  return Array.isArray(value.agreedPolicies);
};

export const isPolicySigned = (status: unknown): boolean => {
  if (status === true) return true;
  if (typeof status === 'string') {
    const normalized = status.trim().toLowerCase();
    return normalized === 'agreed' || normalized === 'true' || normalized === 'signed';
  }
  return false;
};

export const collectParentAgreedPolicies = (
  policies: ParentPoliciesField,
): Array<AgreedPolicy> => {
  if (!policies) return [];

  if (Array.isArray(policies)) {
    if (policies.length === 0) return [];
    if (looksLikePolicyGroup(policies[0])) {
      return (policies as Array<PolicyGroup>).flatMap(
        (group) => group.agreedPolicies ?? [],
      );
    }
    if (looksLikePolicyRow(policies[0])) {
      return policies as Array<AgreedPolicy>;
    }
    return [];
  }

  if (isRecord(policies) && Array.isArray(policies.agreedPolicies)) {
    return policies.agreedPolicies.filter(looksLikePolicyRow) as Array<AgreedPolicy>;
  }

  return [];
};

export const collectUnsignedParentPolicies = (
  policies: ParentPoliciesField,
): Array<AgreedPolicy> =>
  collectParentAgreedPolicies(policies).filter(
    (policy) => !isPolicySigned(policy.status),
  );
